import { useState, useEffect } from 'react';
import { FiSearch, FiHash, FiType } from 'react-icons/fi';
import { getVersions, searchByStrong, searchWord } from '../../utils/bible-api';

/**
 * Componente para búsqueda en la Biblia (por Strong o por palabra)
 */
export default function BibleSearch() {
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [searchType, setSearchType] = useState('word'); // 'word' o 'strong'
  const [searchTerm, setSearchTerm] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadVersions();
  }, []);

  const loadVersions = async () => {
    try {
      const response = await getVersions();
      setVersions(response.data || []);
      if (response.data && response.data.length > 0) {
        setSelectedVersion(response.data[0].id);
      }
    } catch (error) {
      console.error('Error al cargar versiones:', error);
      setError('No se pudieron cargar las versiones');
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchTerm.trim()) {
      setError('Por favor ingresa un término de búsqueda');
      return;
    }

    if (!selectedVersion) {
      setError('Por favor selecciona una versión');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSearched(true);

      let response;
      if (searchType === 'strong') {
        response = await searchByStrong(selectedVersion, searchTerm.trim());
      } else {
        response = await searchWord(selectedVersion, searchTerm.trim(), caseSensitive);
      }

      setResults(response.data.results || []);
    } catch (error) {
      console.error('Error en la búsqueda:', error);
      setError(error.response?.data?.message || 'Error al realizar la búsqueda');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-indigo-600 rounded-lg">
              <FiSearch className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Búsqueda Bíblica
              </h1>
              <p className="text-sm text-gray-600">
                Busca por palabra o número Strong
              </p>
            </div>
          </div>

          {/* Formulario de búsqueda */}
          <form onSubmit={handleSearch} className="space-y-4">
            {/* Tipo de búsqueda */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSearchType('word')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
                  searchType === 'word'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <FiType className="w-5 h-5" />
                Buscar Palabra
              </button>
              <button
                type="button"
                onClick={() => setSearchType('strong')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
                  searchType === 'strong'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <FiHash className="w-5 h-5" />
                Buscar por Strong
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Versión */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Versión
                </label>
                <select
                  value={selectedVersion || ''}
                  onChange={(e) => setSelectedVersion(parseInt(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  {versions.map((version) => (
                    <option key={version.id} value={version.id}>
                      {version.abbreviation} - {version.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Término de búsqueda */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {searchType === 'strong' ? 'Número Strong' : 'Palabra o frase'}
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={searchType === 'strong' ? 'Ej: H430, G2316' : 'Ej: amor, fe, esperanza'}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Opciones adicionales para búsqueda de palabras */}
            {searchType === 'word' && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="caseSensitive"
                  checked={caseSensitive}
                  onChange={(e) => setCaseSensitive(e.target.checked)}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <label htmlFor="caseSensitive" className="text-sm text-gray-700">
                  Distinguir mayúsculas y minúsculas
                </label>
              </div>
            )}

            {/* Botón de búsqueda */}
            <button
              type="submit"
              disabled={loading || !searchTerm.trim()}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              <FiSearch className="w-5 h-5" />
              {loading ? 'Buscando...' : 'Buscar'}
            </button>
          </form>

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">{error}</p>
            </div>
          )}
        </div>

        {/* Resultados */}
        {searched && !loading && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Resultados de la búsqueda
              </h2>
              <span className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm font-medium">
                {results.length} {results.length === 1 ? 'resultado' : 'resultados'}
              </span>
            </div>

            {results.length > 0 ? (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {results.map((result, index) => (
                  <div
                    key={index}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-indigo-50 transition-colors"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold text-indigo-600">
                        {result.Book} {result.Chapter}:{result.Verse}
                      </span>
                    </div>
                    <p className="text-gray-800 leading-relaxed">
                      {highlightSearchTerm(result.Scripture, searchTerm)}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <FiSearch className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600">
                  No se encontraron resultados para &quot;{searchTerm}&quot;
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Resalta el término buscado en el texto
 */
function highlightSearchTerm(text, term) {
  if (!term) return text;
  
  const parts = text.split(new RegExp(`(${term})`, 'gi'));
  return (
    <>
      {parts.map((part, index) => 
        part.toLowerCase() === term.toLowerCase() ? (
          <mark key={index} className="bg-yellow-200 font-semibold">
            {part}
          </mark>
        ) : (
          <span key={index}>{part}</span>
        )
      )}
    </>
  );
}





